import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  User,
  Mail,
  Phone,
  Lock,
  Save,
  Eye,
  EyeOff,
  Shield,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile state
  const [profile, setProfile] = useState({
    email: '',
    telephone: '',
    nom: '',
    prenom: '',
    role: ''
  });
  const [originalProfile, setOriginalProfile] = useState({});

  // Password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/account/settings`);
      setProfile(response.data);
      setOriginalProfile(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const hasProfileChanges = () => {
    return profile.email !== originalProfile.email ||
           profile.telephone !== originalProfile.telephone;
  };

  const handleSaveProfile = async () => {
    if (!hasProfileChanges()) {
      toast.info('Aucune modification a enregistrer');
      return;
    }

    // Validate email
    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      toast.error('Adresse email invalide');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/account/settings`, {
        email: profile.email,
        telephone: profile.telephone
      });
      toast.success('Paramètres mis à jour avec succès');
      setOriginalProfile(profile);

      // Refresh user context if email changed
      if (profile.email !== originalProfile.email && refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validations
    if (!passwordData.current_password) {
      toast.error('Veuillez entrer votre mot de passe actuel');
      return;
    }
    if (!passwordData.new_password) {
      toast.error('Veuillez entrer un nouveau mot de passe');
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caracteres');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API_URL}/account/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Mot de passe modifié avec succès');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      'super_admin': 'Super Administrateur',
      'admin': 'Administrateur',
      'rh': 'Ressources Humaines',
      'comptabilite': 'Comptabilite',
      'exploitation': 'Exploitation',
      'employe': 'Employe'
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Parametres du compte</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Gerez vos informations personnelles et la securite de votre compte
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Profil
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Securite
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#0F5156]" />
                Informations du compte
              </CardTitle>
              <CardDescription>
                Mettez a jour vos coordonnees. Votre email sert a vous connecter.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Read-only info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <Label className="text-xs text-slate-500 dark:text-slate-400">Nom</Label>
                  <p className="font-medium text-slate-900 dark:text-white">{profile.nom}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 dark:text-slate-400">Prenom</Label>
                  <p className="font-medium text-slate-900 dark:text-white">{profile.prenom}</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500 dark:text-slate-400">Role</Label>
                  <p className="font-medium text-slate-900 dark:text-white">{getRoleLabel(profile.role)}</p>
                </div>
              </div>

              <Separator />

              {/* Editable fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    Adresse email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleProfileChange('email', e.target.value)}
                    placeholder="votre@email.com"
                    className="max-w-md"
                  />
                  <p className="text-xs text-slate-500">
                    Cette adresse est utilisee pour vous connecter et recevoir les notifications
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telephone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-500" />
                    Numero de telephone
                  </Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={profile.telephone || ''}
                    onChange={(e) => handleProfileChange('telephone', e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="max-w-md"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveProfile}
                  disabled={saving || !hasProfileChanges()}
                  className="bg-[#0F5156] hover:bg-[#0d464a]"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer les modifications
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#0F5156]" />
                Changer le mot de passe
              </CardTitle>
              <CardDescription>
                Assurez-vous d'utiliser un mot de passe fort et unique
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Mot de passe actuel</Label>
                  <div className="relative">
                    <Input
                      id="current_password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                      placeholder="Entrez votre mot de passe actuel"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_password">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="new_password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                      placeholder="Minimum 8 caracteres"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordData.new_password && passwordData.new_password.length < 8 && (
                    <p className="text-xs text-red-500">Le mot de passe doit contenir au moins 8 caracteres</p>
                  )}
                  {passwordData.new_password && passwordData.new_password.length >= 8 && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Longueur suffisante
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                      placeholder="Confirmez votre nouveau mot de passe"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                    <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                  )}
                  {passwordData.confirm_password && passwordData.new_password === passwordData.confirm_password && passwordData.new_password.length >= 8 && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Les mots de passe correspondent
                    </p>
                  )}
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={changingPassword || !passwordData.current_password || !passwordData.new_password || passwordData.new_password !== passwordData.confirm_password}
                    className="bg-[#0F5156] hover:bg-[#0d464a]"
                  >
                    {changingPassword ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Modification...
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Changer le mot de passe
                      </>
                    )}
                  </Button>
                </div>
              </form>

              <Separator className="my-6" />

              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">Conseils de securite</h4>
                <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                  <li>• Utilisez au moins 8 caracteres</li>
                  <li>• Combinez lettres majuscules, minuscules, chiffres et caracteres speciaux</li>
                  <li>• Ne reutilisez pas vos mots de passe sur d'autres sites</li>
                  <li>• Changez regulierement votre mot de passe</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
